import { css } from '@emotion/react';
import { ZoomBehavior } from 'd3-zoom';
import React, { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';

import { InteractionType } from '$/GraphQL/schema/schema';
import { WebinarPlayerContext } from '$/components/WebinarPlayer/context';
import type { InteractionInfo } from '$/components/Interactions/InteractionInfo';

import { MarkerData } from './types';
import Marker from '../Marker/Marker';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { ZoomScaleProviderContext } from '../Timeline/ZoomScaleProviderContext';

function mergeMarkers(
  zoom: ZoomBehavior<SVGSVGElement, unknown>,
  interactions: MarkerData[]
): MarkerData[] {
  return interactions
    .map((d: MarkerData) => {
      const count = d.count || 1;
      if (d.x < 0) {
        return {
          ...d,
          x: 0,
          count,
        };
      }

      const width = zoom.translateExtent()[1][0];
      if (d.x > width) {
        return {
          ...d,
          x: width,
          count,
        };
      }

      return d;
    })
    .reduce<{ start: MarkerData | null; items: MarkerData[] }>(
      (mergedMarkers, marker) => {
        if (!mergedMarkers.start) {
          return {
            start: marker,
            items: [marker],
            startX: marker.showAt,
            endX: marker.showAt,
          };
        }
        const width = zoom.translateExtent()[1][0];

        const distance = mergedMarkers.start && Math.abs(marker.x - mergedMarkers.start.x);
        if (distance < 10) {
          const count = mergedMarkers.start.count ? mergedMarkers.start.count + 1 : 2;
          const startX = mergedMarkers.start.grpStartAt ?? marker.showAt;
          const endX = marker.showAt;
          const nextX = (endX + startX) / 2;

          const items = mergedMarkers.start.items
            ? mergedMarkers.start.items.concat([marker])
            : [mergedMarkers.start, marker];

          return {
            start: {
              ...mergedMarkers.start,
              count,
              items,
            },
            items: mergedMarkers.items.slice(0, mergedMarkers.items.length - 1).concat({
              ...mergedMarkers.start,

              playbackPosition: marker.playbackPosition,
              endsAt: marker.endsAt,

              showAt: nextX >= width ? mergedMarkers.start.showAt : nextX,
              grpStartAt: startX,
              grpEndAt: endX,
              x: marker.x,
              items,
              count,
            }),
          };
        }
        return {
          start: marker,
          items: mergedMarkers.items.concat(marker),
        };
      },
      { start: null, items: [] }
    ).items;
}

function useMarkersFromInteractions(interactions: InteractionInfo[]): MarkerData[] {
  const { zoomScale, zoom, x } = React.useContext(ZoomScaleProviderContext);
  const { interactionInEdit, timelineComponents } = React.useContext(WebinarPlayerContext);
  const formHandlers = useFormContext();

  const formPlaybackPosition = formHandlers?.watch('details.playbackPosition');
  const formEndsAt = formHandlers?.watch('details.endsAt');
  const formPlaybackTitle = formHandlers?.watch('title');

  return React.useMemo(() => {
    if (!interactions) return [];

    // Calculate which interactions to bring together into the same data point
    // Circles that say 2 or more

    const shownComponents = Object.keys(timelineComponents);
    const markers = interactions
      .filter((i) => shownComponents.includes(i.timelineComponent())) // Filter out markers for components we aren't showing
      .map((i) => {
        let position =
          interactionInEdit?.id === i.id && i.hasEndsAt()
            ? i.endsAtPlaybackPosition
            : i.playbackPosition;
        let endsAt = i.hasEndsAt() ? i.endsAtPlaybackPosition : position;

        if (interactionInEdit?.id === i.id) {
          // If we're currently editing an interaction then pull the current value of
          // playbackPosition from the form not the interaction
          position = formPlaybackPosition;
          endsAt = formEndsAt;
        }

        const datum: MarkerData = {
          interaction: i,
          timeline: i.timelineComponent(),
          x: zoomScale ? zoomScale(position) ?? 1 : x!(position) ?? 0,

          playbackPosition: position,
          endsAt,

          showAt: position,
          grpStartAt: position,
          grpEndAt: position,

          title: formPlaybackTitle ?? i.title(),
        };
        return datum;
      });

    let allMarkers: MarkerData[] = [];
    const timelines = new Set(markers.map((m) => m.timeline));

    for (const timeline of timelines.values()) {
      allMarkers = allMarkers.concat(
        mergeMarkers(
          zoom!,
          markers.filter((m) => m.timeline === timeline)
        )
      );
    }

    return allMarkers;
  }, [
    formEndsAt,
    formPlaybackPosition,
    formPlaybackTitle,
    interactionInEdit?.id,
    interactions,
    timelineComponents,
    x,
    zoom,
    zoomScale,
  ]);
}

const Markers = ({
  isEditingInteractions,
  hasPermission,
  showPermissionModal,
}: {
  isEditingInteractions?: boolean;
  hasPermission: boolean;
  showPermissionModal: () => void;
}): ReactElement => {
  const {
    interactions: interactionsInWebinar,
    interactionInEdit,
    onInteractionIconClick,
  } = React.useContext(WebinarPlayerContext);

  // If editing 1 interaction, only show markers for this one
  let interactions = interactionsInWebinar;
  if (interactionInEdit) {
    interactions = [interactionInEdit];
  }

  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  const [hoveringId, setHoveringId] = React.useState<string | null>(null);
  const markers = useMarkersFromInteractions(interactions);
  const etheme = useThemeContext();

  const otherMarkers = markers.filter(
    (m) => m.x !== undefined && m.x >= 0 && m.interaction.type !== InteractionType.Anchor
  );
  const anchorMarkers = markers.filter(
    (m) => m.x !== undefined && m.x >= 0 && m.interaction.type === InteractionType.Anchor
  );

  return (
    <g
      cursor='pointer'
      css={css`
        .icon {
          overflow: visible;
          color: ${etheme.player.timeline.marker.fg};
        }
      `}
    >
      <defs>
        <filter id='Blur' xmlns='http://www.w3.org/2000/svg' height='130%' width='130%'>
          <feGaussianBlur in='SourceAlpha' stdDeviation='3'>
            {/* <animate attributeName='stdDeviation' values='0;3' dur='0.3s' /> */}
          </feGaussianBlur>

          <feOffset dx='2' dy='2' result='offsetblur' />
          <feComponentTransfer>
            <feFuncA type='linear' slope='0.2'>
              <animate attributeName='stdDeviation' values='0;0.2' dur='0.4s' />
            </feFuncA>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in='SourceGraphic' />
          </feMerge>
        </filter>
      </defs>

      {anchorMarkers?.map((marker, index) => (
        /* Draw anchors first so they're above other markers */
        <Marker
          key={marker.interaction.id ?? index}
          marker={marker}
          isDraggable={Boolean(isEditingInteractions)}
          hasPermission={hasPermission}
          showPermissionModal={showPermissionModal}
          draggingId={draggingId}
          setDraggingId={setDraggingId}
          hoveringId={hoveringId}
          setHoveringId={setHoveringId}
          onInteractionIconClick={onInteractionIconClick}
        />
      ))}

      {otherMarkers?.map((marker, index) => (
        <Marker
          key={marker.interaction.id ?? index}
          marker={marker}
          isDraggable={Boolean(isEditingInteractions)}
          hasPermission={hasPermission}
          showPermissionModal={showPermissionModal}
          draggingId={draggingId}
          setDraggingId={setDraggingId}
          hoveringId={hoveringId}
          setHoveringId={setHoveringId}
          onInteractionIconClick={onInteractionIconClick}
        />
      ))}
    </g>
  );
};

export default Markers;
